import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analyticsService';
import { useLanguage } from '../../contexts/LanguageContext';
import { Users, DollarSign, Activity, MessageCircle, TrendingUp, TrendingDown, Zap, Clock, BarChart3, PieChart } from 'lucide-react';

const GeneralStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('today');
    const { t } = useLanguage();

    useEffect(() => {
        loadStats();
    }, [period]);

    const loadStats = async () => {
        try {
            setLoading(true);
            const response = await analyticsService.getSystemStats(period);
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error(t('loadStatsError'), error);
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ icon, title, value, trend, subtitle, color = 'primary' }) => (
        <div className={`stat-card stat-card--${color}`}>
            <div className="stat-icon">{icon}</div>
            <div className="stat-content">
                <h3>{value}</h3>
                <p>{title}</p>
                {trend && (
                    <div className={`stat-trend ${trend > 0 ? 'positive' : 'negative'}`}>
                        {trend > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                        <span>{Math.abs(trend)}%</span>
                    </div>
                )}
                {subtitle && <div className="stat-subtitle">{subtitle}</div>}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <div>{t('loading')}</div>
            </div>
        );
    }

    if (!stats) {
        return <div className="no-data">{t('noStatsAvailable')}</div>;
    }

    const userStats = stats.userStatistics;
    const paymentStats = stats.paymentStatistics;
    const pomodoroStats = stats.pomodoroStatistics;
    const aiStats = stats.aiStatistics;

    return (
        <div className="general-stats">
            {/* Period Selector */}
            <div className="period-selector">
                <div className="period-buttons">
                    {['today', 'week', 'month', 'year'].map(p => (
                        <button
                            key={p}
                            className={`period-button ${period === p ? 'active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {t(p)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Metrics Grid */}
            <div className="metrics-grid">
                <StatCard
                    icon={<Users size={28} />}
                    title={t('totalUsers')}
                    value={userStats?.total_users?.toLocaleString() || '0'}
                    trend={12.5}
                    color="blue"
                />
                <StatCard
                    icon={<DollarSign size={28} />}
                    title={t('totalRevenue')}
                    value={`${(paymentStats?.total_revenue || 0).toLocaleString()} TL`}
                    trend={8.3}
                    color="green"
                />
                <StatCard
                    icon={<Activity size={28} />}
                    title={t('activeUsers')}
                    value={(pomodoroStats?.active_users || 0).toLocaleString()}
                    trend={15.2}
                    color="purple"
                />
                <StatCard
                    icon={<MessageCircle size={28} />}
                    title={t('totalMessages')}
                    value={(aiStats?.total_conversations || 0).toLocaleString()}
                    trend={22.7}
                    color="orange"
                />
            </div>

            {/* Detailed Analytics */}
            <div className="analytics-grid">
                <div className="analytics-card">
                    <div className="card-header">
                        <BarChart3 size={24} />
                        <h3>{t('userGrowth')}</h3>
                    </div>
                    <div className="growth-chart-placeholder">
                        <div className="chart-content">
                            <p>📈 {t('chartPlaceholder')}</p>
                            <div className="growth-stats">
                                <div className="growth-stat">
                                    <span className="label">{t('newUsersToday')}</span>
                                    <span className="value">{userStats?.new_users_today || 0}</span>
                                </div>
                                <div className="growth-stat">
                                    <span className="label">{t('premiumUsers')}</span>
                                    <span className="value">
                                        {(userStats?.byPlan || []).filter(p => p.slug !== 'free').reduce((sum, p) => sum + Number(p.count), 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="analytics-card">
                    <div className="card-header">
                        <PieChart size={24} />
                        <h3>{t('planDistribution')}</h3>
                    </div>
                    <div className="distribution-chart-placeholder">
                        <div className="distribution-stats">
                            {(userStats?.byPlan || []).map(p => (
                                <div className="plan-stat" key={p.slug}>
                                    <span className={`plan-badge ${p.slug}`}>{p.name}</span>
                                    <span className="plan-count">{p.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="analytics-card full-width">
                    <div className="card-header">
                        <Zap size={24} />
                        <h3>{t('systemMetrics')}</h3>
                    </div>
                    <div className="system-metrics">
                        <div className="metric-row">
                            <div className="metric">
                                <Clock size={20} />
                                <span>{t('totalSessions')}</span>
                                <strong>{pomodoroStats?.total_sessions?.toLocaleString() || 0}</strong>
                            </div>
                            <div className="metric">
                                <MessageCircle size={20} />
                                <span>{t('totalTokens')}</span>
                                <strong>{(aiStats?.total_tokens || 0).toLocaleString()}</strong>
                            </div>
                            <div className="metric">
                                <Activity size={20} />
                                <span>{t('avgSessionDuration')}</span>
                                <strong>{Math.round(pomodoroStats?.avg_session_duration || 0)} dk</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeneralStats;